import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import './Step1CustomerInfo.scss';
import { CustomIcon } from '../../../UI';
import DatePickerField from '../../calendar/DatePickerField';
import customerService from '../../../../services/customerService';
import { filterEmptyArrayItems } from '../../../../utils/validation';
import { X } from 'lucide-react';
import CustomerEmailsSection from './components/CustomerEmailsSection';
import Loading from '../../../loading-animation';

const Step1CustomerInfo = ({ readOnly = false }) => {
  const { register, formState: { errors }, setValue, watch, getValues } = useFormContext();
  
  // Get current data from form
  const formCustomerCompanyName = watch('customer.companyName');
  const formCustomerAddress = watch('customer.customerAddress');
  const formRepresentativePeoples = watch('customer.representativePeoples') || [];
  const customerType = watch('customer.type') || 'customer';
  
  // State for search
  const [searchType, setSearchType] = useState('email'); // 'email' or 'companyName'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);

  useEffect(() => {
    if (!isInitialized && formCustomerCompanyName) {
      const formValues = getValues();
      const customerTypeFromForm = formValues.customer?.type || customerType || 'customer';
      const customerFromForm = {
        id: formValues.customer?.id || null,
        companyName: formCustomerCompanyName,
        type: customerTypeFromForm,
        customerAddress: formCustomerAddress || {},
        phoneNumber: formValues.customer?.phoneNumber || '',
        email: formValues.customer?.email || '',
        paymentMethod: formValues.customer?.paymentMethod || '',
        paymentTerms: formValues.customer?.paymentTerms || '',
        creditLimit: formValues.customer?.creditLimit || null,
        representativePeoples: formRepresentativePeoples || []
      };
      setSelectedCustomer(customerFromForm);
      // Если это платформа и есть companyName, убеждаемся что type установлен
      if (customerTypeFromForm === 'platform' && formCustomerCompanyName) {
        setValue('customer.type', 'platform', { shouldDirty: false });
      }
      setIsInitialized(true);
    }
  }, [formCustomerCompanyName, formCustomerAddress, customerType, formRepresentativePeoples, isInitialized, getValues, setValue]);

  const getCustomerEmails = (customer) => {
    const customerEmails = Array.isArray(customer.emails) ? customer.emails : [];
    const primaryEmail = customer.email || '';
    return [...new Set([...customerEmails, primaryEmail].filter(Boolean))];
  };

  // Search customers
  const searchCustomers = useCallback(async (query, type) => {
    if (readOnly) return;
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const searchFilters = { limit: 10 };
      
      if (type === 'email') {
        searchFilters.email = query.trim();
      } else if (type === 'companyName') {
        searchFilters.companyName = query.trim();
      } else {
        searchFilters.q = query.trim();
      }
      
      const response = await customerService.searchCustomers(searchFilters);
      const customers = response?.data?.data || response?.data || [];
      setSearchResults(customers);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching customers:', error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  }, [readOnly]);

  // Debounced search
  useEffect(() => {
    if (readOnly) return;
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchCustomers(searchQuery, searchType);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType, searchCustomers, readOnly]);

  // Handle search input change
  const handleSearchChange = (e) => {
    if (readOnly) return;
    const value = e.target.value;
    setSearchQuery(value);
    if (!value) {
      setShowSearchResults(false);
    }
  };

  // Handle search type change
  const handleSearchTypeChange = (e) => {
    if (readOnly) return;
    setSearchType(e.target.value);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Handle customer selection from search results
  const handleCustomerSelect = (customer) => {
    if (readOnly) return;
    // Store full customer data including payment info and representativePeoples
    const customerWithPayment = {
      ...customer,
      paymentMethod: customer.paymentMethod || '',
      paymentTerms: customer.paymentTerms || '',
      creditLimit: customer.creditLimit || null,
      representativePeoples: customer.representativePeoples || [],
      type: customer.type || 'customer'
    };
    setSelectedCustomer(customerWithPayment);
    setShowSearchResults(false);
    setSearchQuery('');

    // Fill customer fields with shouldDirty: true
    setValue('customer.id', customer._id || customer.id || null, { shouldDirty: true });
    setValue('customer.type', customer.type || 'customer', { shouldDirty: true });
    setValue('customer.companyName', customer.companyName || '', { shouldDirty: true });
    setValue('customer.email', customer.email || '', { shouldDirty: true });
    setValue('customerEmails', getCustomerEmails(customer), { shouldDirty: true, shouldValidate: true });
    
    // Fill address fields
    const address = customer.customerAddress || customer.address || {};
    setValue('customer.customerAddress.address', address.address || '', { shouldDirty: true });
    setValue('customer.customerAddress.city', address.city || '', { shouldDirty: true });
    setValue('customer.customerAddress.state', address.state || '', { shouldDirty: true });
    setValue('customer.customerAddress.zipCode', address.zipCode || address.zip || '', { shouldDirty: true });

    // Fill payment information if available
    if (customer.paymentMethod) {
      setValue('customer.paymentMethod', customer.paymentMethod, { shouldDirty: true });
    }
    if (customer.paymentTerms) {
      setValue('customer.paymentTerms', customer.paymentTerms, { shouldDirty: true });
    }
    if (customer.creditLimit) {
      setValue('customer.creditLimit', customer.creditLimit.toString(), { shouldDirty: true });
    }

    // Email уже установлен выше через setValue('customer.email', ...)

    const loadRepPeoples = Array.isArray(customer.representativePeoples) && customer.representativePeoples.length > 0
      ? filterEmptyArrayItems(
          customer.representativePeoples.map(person => ({
            fullName: person.fullName || '',
            email: person.email || '',
            phoneNumber: person.phoneNumber || ''
          })),
          { customFilter: (person) => person.fullName && person.fullName.trim() }
        ) || []
      : [];
    setValue('customer.representativePeoples', loadRepPeoples, { shouldDirty: true });
  };

  // Get customer email (single email, not array)
  const getCustomerEmail = (customer) => {
    return customer.email || '';
  };

  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear selected customer
  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setValue('customer.id', null, { shouldDirty: true });
    // Don't reset type - let toggle buttons handle it
    setValue('customer.companyName', '', { shouldDirty: true });
    setValue('customer.email', '', { shouldDirty: true });
    setValue('customerEmails', [], { shouldDirty: true, shouldValidate: true });
    setValue('customer.customerAddress.address', '', { shouldDirty: true });
    setValue('customer.customerAddress.city', '', { shouldDirty: true });
    setValue('customer.customerAddress.state', '', { shouldDirty: true });
    setValue('customer.customerAddress.zipCode', '', { shouldDirty: true });
    setValue('customer.paymentMethod', '', { shouldDirty: true });
    setValue('customer.paymentTerms', '', { shouldDirty: true });
    setValue('customer.creditLimit', '', { shouldDirty: true });
    setValue('customer.representativePeoples', [], { shouldDirty: true });
  };



  // Representative Peoples handlers
  const handleAddRepresentativePerson = () => {
    const fullNameInput = document.getElementById('loadRepFullName');
    const emailInput = document.getElementById('loadRepEmail');
    const phoneInput = document.getElementById('loadRepPhone');
    const fullName = fullNameInput?.value.trim();
    
    if (fullName) {
      const newPerson = {
        fullName: fullName,
        email: emailInput?.value.trim() || '',
        phoneNumber: phoneInput?.value.trim() || ''
      };
      const currentPeoples = formRepresentativePeoples || [];
      const newPeoples = [...currentPeoples, newPerson];
      // Filter empty values before setting
      const filteredPeoples = filterEmptyArrayItems(newPeoples, {
        customFilter: (person) => person.fullName && person.fullName.trim()
      });
      setValue('customer.representativePeoples', filteredPeoples || [], { shouldDirty: true });
      if (fullNameInput) fullNameInput.value = '';
      if (emailInput) emailInput.value = '';
      if (phoneInput) phoneInput.value = '';
    }
  };

  const handleRemoveRepresentativePerson = (index) => {
    const currentPeoples = formRepresentativePeoples || [];
    const newPeoples = currentPeoples.filter((_, i) => i !== index);
    // Filter empty values before setting
    const filteredPeoples = filterEmptyArrayItems(newPeoples, {
      customFilter: (person) => person.fullName && person.fullName.trim()
    });
    setValue('customer.representativePeoples', filteredPeoples || [], { shouldDirty: true });
  };

  // Handle platform selection
  const handlePlatformSelect = async (e) => {
    const platformName = e.target.value;
    if (!platformName) {
      clearSelectedCustomer();
      return;
    }

    try {
      setIsSearching(true);
      // Ищем customer с type='platform' и companyName=platformName
      const response = await customerService.searchCustomers({
        companyName: platformName,
        type: 'platform',
        limit: 1
      });
      
      const customers = response?.data?.data || response?.data || [];
      let platformCustomer = customers.find(c => 
        c.type === 'platform' && 
        c.companyName.toLowerCase() === platformName.toLowerCase()
      );

      // Если не найден, создадим его при сохранении load (через findOrCreate)
      // Но пока заполним форму данными
      if (platformCustomer) {
        handleCustomerSelect(platformCustomer);
      } else {
        // Создаем временный объект customer для формы
        const newPlatformCustomer = {
          companyName: platformName,
          type: 'platform',
          customerAddress: null,
          email: null,
          phoneNumber: '',
          paymentMethod: '',
          paymentTerms: '',
          creditLimit: null,
          representativePeoples: []
        };
        setSelectedCustomer(newPlatformCustomer);
        setValue('customer.id', null, { shouldDirty: true });
        setValue('customer.type', 'platform', { shouldDirty: true });
        setValue('customer.companyName', platformName, { shouldDirty: true });
        setValue('customer.customerAddress', null, { shouldDirty: true });
      }
    } catch (error) {
      console.error('Error searching platform customer:', error);
      // В случае ошибки все равно заполняем форму
      const newPlatformCustomer = {
        companyName: platformName,
        type: 'platform',
        customerAddress: null,
        email: null,
        phoneNumber: '',
        paymentMethod: '',
        paymentTerms: '',
        creditLimit: null,
        representativePeoples: []
      };
      setSelectedCustomer(newPlatformCustomer);
      setValue('customer.id', null, { shouldDirty: true });
      setValue('customer.type', 'platform', { shouldDirty: true });
      setValue('customer.companyName', platformName, { shouldDirty: true });
      setValue('customer.customerAddress', null, { shouldDirty: true });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="step-1-customer-info">
      <fieldset disabled={readOnly}>
      <h3 className="form-title">Add New Load</h3>
      
      {/* Customer Type Selection */}
      <div className="customer-type-toggle">
        <label className="toggle-label">Customer Type</label>
        <div className="toggle-buttons">
          <button
            type="button"
            onClick={() => {
              setValue('customer.type', 'customer', { shouldDirty: true, shouldValidate: true });
              clearSelectedCustomer();
              setSearchQuery('');
              setSearchResults([]);
              setShowSearchResults(false);
            }}
            className={customerType === 'customer' ? 'active' : ''}
          >
            Customer
          </button>
          <button
            type="button"
            onClick={() => {
              setValue('customer.type', 'platform', { shouldDirty: true, shouldValidate: true });
              clearSelectedCustomer();
              setSearchQuery('');
              setSearchResults([]);
              setShowSearchResults(false);
            }}
            className={customerType === 'platform' ? 'active' : ''}
          >
            Platform
          </button>
        </div>
        <input
          type="hidden"
          {...register('customer.type', { required: true })}
          value={customerType}
        />
        {errors.customer?.type && (
          <span className="error-message">{errors.customer.type.message}</span>
        )}
      </div>

      {/* Customer Search Section - Only show for customer type */}
      {customerType === 'customer' && (
        <div className="customer-search-section">
          <h3 className="section-title">Search Customer</h3>
        <div className="search-controls">
          <select
            value={searchType}
            onChange={handleSearchTypeChange}
            className="search-type-select"
          >
            <option value="email">Email</option>
            <option value="companyName">Company Name</option>
          </select>
          <div className="search-input-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={
                searchType === 'email' 
                  ? 'Search by Email...' 
                  : 'Search by Company Name...'
              }
              className="customer-search-input"
            />
            {isSearching && <Loading size="small" inline />}
          </div>
        </div>

        {/* Search Results */}
        {showSearchResults && searchResults.length > 0 && (
          <div ref={searchResultsRef} className="search-results">
            {searchResults.map((customer) => {
              const customerEmail = getCustomerEmail(customer);
              
              return (
                <div
                  key={customer._id || customer.id}
                  className="search-result-item"
                >
                  <div 
                    className="result-main"
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    <div className="result-info">
                      <strong>{customer.companyName || 'N/A'}</strong>
                      <div className="result-details">
                        {customer.customerAddress?.city && customer.customerAddress?.state && (
                          <span>{customer.customerAddress.city}, {customer.customerAddress.state}</span>
                        )}
                        {customerEmail && (
                          <span className="email-count">
                            {customerEmail}
                          </span>
                        )}
                      </div>
                    </div>
                    <CustomIcon name="right" size={16} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showSearchResults && searchResults.length === 0 && searchQuery && !isSearching && (
          <div className="no-results">
            No customers found. You can fill in the details manually.
          </div>
        )}
        </div>
      )}

      {/* Platform Selection - Only show for platform type */}
      {customerType === 'platform' && (
        <div className="customer-search-section">
          <h3 className="section-title">Choose Platform</h3>
          <div className="search-controls">
            <select
              {...register('customer.companyName', {
                required: customerType === 'platform' ? 'Company name is required' : false,
              })}
              className="search-type-select"
              value={formCustomerCompanyName || ""}
              onChange={handlePlatformSelect}
            >
              <option value="">Select Platform</option>
              <option value="Shiply">Shiply</option>
              <option value="CitizenShipper">CitizenShipper</option>
              <option value="Out">Out</option>
            </select>
            {isSearching && <Loading size="small" inline />}
          </div>
          {errors.customer?.companyName && (
            <span className="error-message">{errors.customer.companyName.message}</span>
          )}
        </div>
      )}

      {/* Selected Customer Info */}
      {selectedCustomer && (
        <div className="selected-customer-info">
          <div className="selected-customer-header">
            <h4>Selected Customer:</h4>
            <button
              type="button"
              className="clear-customer-btn"
              onClick={clearSelectedCustomer}
            >
              Clear
            </button>
          </div>
          <div className="selected-customer-details">
            <p><strong>Company:</strong> {selectedCustomer.companyName}</p>
            {selectedCustomer.customerAddress && (
              <p><strong>Address:</strong> {
                [
                  selectedCustomer.customerAddress.address,
                  selectedCustomer.customerAddress.city,
                  selectedCustomer.customerAddress.state,
                  selectedCustomer.customerAddress.zipCode
                ].filter(Boolean).join(', ')
              }</p>
            )}
            {(selectedCustomer.paymentMethod || selectedCustomer.paymentTerms || selectedCustomer.creditLimit) && (
              <div className="customer-payment-preview">
                <p className="payment-preview-title"><strong>Payment Information:</strong></p>
                {selectedCustomer.paymentMethod && (
                  <p><strong>Method:</strong> {selectedCustomer.paymentMethod}</p>
                )}
                {selectedCustomer.paymentTerms && (
                  <p><strong>Terms:</strong> {selectedCustomer.paymentTerms}</p>
                )}
                {selectedCustomer.creditLimit && (
                  <p><strong>Credit Limit:</strong> ${parseFloat(selectedCustomer.creditLimit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {customerType === 'customer' && (
        <div className="form-grid">
          <div className="form-group form-group--full">
            <label htmlFor="customer.companyName">
              Company Name
            </label>
            <input
              type="text"
              id="customer.companyName"
              placeholder="Text"
              {...register('customer.companyName', {
                required: 'Company name is required',
              })}
              className={errors.customer?.companyName ? 'error' : ''}
            />
            {errors.customer?.companyName && (
              <span className="error-message">{errors.customer.companyName.message}</span>
            )}
          </div>
        </div>
      )}

      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="orderId">
            Order ID
          </label>
          <input
            type="text"
            id="orderId"
            placeholder="CC-0000"
            {...register('orderId')}
            className={errors.orderId ? 'error' : ''}
          />
          {errors.orderId && (
            <span className="error-message">{errors.orderId.message}</span>
          )}
        </div>
        {customerType === 'customer' && (
          <div className="form-group">
            <label htmlFor="customer.email">
              Email
            </label>
            <input
              type="email"
              id="customer.email"
              placeholder="email@example.com"
              {...register('customer.email', {
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Please enter a valid email address'
                }
              })}
              className={errors.customer?.email ? 'error' : ''}
            />
            {errors.customer?.email && (
              <span className="error-message">{errors.customer.email.message}</span>
            )}
          </div>
        )}
      </div>

      {customerType === 'customer' && (
        <div className="customer-address-section">
          <h3 className="section-title">Customer Address</h3>
          <div className="form-grid form-grid--four">
          <div className="form-group">
            <label htmlFor="customer.customerAddress.address">
              Address
            </label>
            <input
              type="text"
              id="customer.customerAddress.address"
              placeholder="Text"
              {...register('customer.customerAddress.address')}
              className={errors.customer?.customerAddress?.address ? 'error' : ''}
            />
            {errors.customer?.customerAddress?.address && (
              <span className="error-message">{errors.customer.customerAddress.address.message}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="customer.customerAddress.city">
              City
            </label>
            <input
              type="text"
              id="customer.customerAddress.city"
              placeholder="Text"
              {...register('customer.customerAddress.city')}
              className={errors.customer?.customerAddress?.city ? 'error' : ''}
            />
            {errors.customer?.customerAddress?.city && (
              <span className="error-message">{errors.customer.customerAddress.city.message}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="customer.customerAddress.state">
              State
            </label>
            <input
              type="text"
              id="customer.customerAddress.state"
              placeholder="Text"
              {...register('customer.customerAddress.state')}
              className={errors.customer?.customerAddress?.state ? 'error' : ''}
            />
            {errors.customer?.customerAddress?.state && (
              <span className="error-message">{errors.customer.customerAddress.state.message}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="customer.customerAddress.zipCode">
              Zip
            </label>
            <input
              type="text"
              id="customer.customerAddress.zipCode"
              placeholder="Text"
              {...register('customer.customerAddress.zipCode')}
              className={errors.customer?.customerAddress?.zipCode ? 'error' : ''}
            />
            {errors.customer?.customerAddress?.zipCode && (
              <span className="error-message">{errors.customer.customerAddress.zipCode.message}</span>
            )}
          </div>
        </div>
        </div>
      )}

      <div className="form-grid">
        {customerType === 'customer' && (
          <>
            <div className="form-group">
              <DatePickerField
                name="dates.assignedDate"
                label="Assigned Date"
                placeholder="mm/dd/yyyy"
              />
            </div>
            <div className="form-group">
              <DatePickerField
                name="dates.deadline"
                label="Deadline"
                placeholder="mm/dd/yyyy"
              />
            </div>
          </>
        )}
        <div className="form-group">
          <label htmlFor="customerRate">
            Customer Rate ($)
          </label>
          <input
            type="number"
            id="customerRate"
            placeholder="Rate"
            {...register('customerRate')}
            className={errors.customerRate ? 'error' : ''}
            onWheel={(e) => e.target.blur()}
          />
          {errors.customerRate && (
            <span className="error-message">{errors.customerRate.message}</span>
          )}
        </div>
      </div>

      {/* {customerType === 'customer' && (
        <CustomerEmailsSection />
      )} */}

      {/* Payment Information Section */}
      {/* <div className="customer-payment-section">
        <h3 className="section-title">Payment Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="customer.paymentMethod">
              Payment Method
            </label>
            <select
              id="customer.paymentMethod"
              {...register('customer.paymentMethod')}
              className={errors.customer?.paymentMethod ? 'error' : ''}
            >
              <option value="">Select Payment Method</option>
              <option value="ACH">ACH</option>
              <option value="Wire">Wire</option>
              <option value="Check">Check</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Net 30">Net 30</option>
              <option value="Net 60">Net 60</option>
              <option value="Net 90">Net 90</option>
              <option value="COD">COD</option>
              <option value="Factoring">Factoring</option>
              <option value="Other">Other</option>
            </select>
            {errors.customer?.paymentMethod && (
              <span className="error-message">{errors.customer.paymentMethod.message}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="customer.creditLimit">
              Credit Limit ($)
            </label>
            <input
              type="number"
              id="customer.creditLimit"
              placeholder="0.00"
              {...register('customer.creditLimit')}
              className={errors.customer?.creditLimit ? 'error' : ''}
              min="0"
              onWheel={(e) => e.target.blur()}
              step="0.01"
            />
            {errors.customer?.creditLimit && (
              <span className="error-message">{errors.customer.creditLimit.message}</span>
            )}
          </div>
        </div>
        <div className="form-grid form-grid--single">
          <div className="form-group">
            <label htmlFor="customer.paymentTerms">
              DTP
            </label>
            <textarea
              id="customer.paymentTerms"
              placeholder="Payment due within 30 days of invoice date..."
              {...register('customer.paymentTerms')}
              className={errors.customer?.paymentTerms ? 'error' : ''}
              rows={3}
            />
            {errors.customer?.paymentTerms && (
              <span className="error-message">{errors.customer.paymentTerms.message}</span>
            )}
          </div>
        </div>
      </div> */}


      {/* Representative People Section */}
      {customerType === 'customer' && (
        <div className="customer-representative-peoples-section">
        <h3 className="section-title">Representative People</h3>
        <label className="form-label">
          Add contact persons for this customer (optional)
        </label>
        
        {formRepresentativePeoples.length > 0 && (
          <div className="selected-items-list">
            {formRepresentativePeoples.map((person, index) => {
              const fullName = person.fullName || '';
              const email = person.email || '';
              const phoneNumber = person.phoneNumber || '';
              
              return (
                <div key={`person-${index}-${fullName}`} className="selected-item-tag">
                  <span>
                    {fullName}
                    {email && ` - ${email}`}
                    {phoneNumber && ` - ${phoneNumber}`}
                  </span>
                  <button
                    type="button"
                    className="remove-tag-button"
                    onClick={() => handleRemoveRepresentativePerson(index)}
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="add-person-form">
          <h4>Add New Person</h4>
          <div>
            <div className="form-group">
              <label htmlFor="loadRepFullName" className="form-label">Full Name *</label>
              <input
                type="text"
                placeholder="Full Name"
                id="loadRepFullName"
                className="form-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRepresentativePerson();
                  }
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="loadRepEmail" className="form-label">Email</label>
              <input
                type="email"
                placeholder="email@example.com"
                id="loadRepEmail"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="loadRepPhone" className="form-label">Phone Number</label>
              <input
                type="tel"
                placeholder="Phone Number"
                id="loadRepPhone"
                className="form-input"
              />
            </div>
            <button
              type="button"
              onClick={handleAddRepresentativePerson}
              className="add-person-form__button"
            >
              Add Person
            </button>
          </div>
        </div>
        </div>
      )}
        {customerType === 'customer' && (
        <CustomerEmailsSection />
      )}
      </fieldset>
    </div>
  );
};

export default Step1CustomerInfo;
